'use client';

import { Box, HStack } from '@terra/ui-styled-system';
import { Text } from '../../components/text';
import { Input } from '../../components/input';
import { IconButton } from '../../components/icon-button';
import { Button } from '../../components/button';
import { Tooltip } from '../../components/tooltip';
import {
  LetterSpacingIcon,
  ArrowDown01Icon,
  ArrowLeft01Icon,
  ArrowRight01Icon,
  ArrowUp01Icon,
  CodeIcon,
  ReplaceIcon,
  ReplaceAllIcon,
  Search01Icon,
  Cancel01Icon,
} from '@terra/ui-icons';
import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import type { HttpEditorSearchBarProps } from './types';

/**
 * Search bar component for HttpEditor
 * Supports regex, case sensitivity, and find/replace functionality
 */
export function HttpEditorSearchBar({
  searchTerm,
  onSearchTermChange,
  isRegex,
  onRegexToggle,
  isCaseSensitive,
  onCaseSensitiveToggle,
  currentMatchIndex,
  matchCount,
  onNextMatch,
  onPrevMatch,
  onClose,
  searchInputRef,
  isReplaceExpanded,
  onReplaceExpandedToggle,
  replaceTerm = '',
  onReplaceTermChange,
  onReplace,
  onReplaceAll,
  hideReplace,
}: HttpEditorSearchBarProps) {
  const t = useTranslations('designSystem.httpEditor');
  const showReplaceRow = Boolean(isReplaceExpanded) && !hideReplace;
  const handleSearchKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        onClose();
      } else if (event.key === 'Enter') {
        if (event.shiftKey) {
          onPrevMatch();
        } else {
          onNextMatch();
        }
      }
    },
    [onClose, onNextMatch, onPrevMatch],
  );
  const handleReplaceKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        onClose();
      } else if (event.key === 'Enter') {
        if (event.metaKey || event.ctrlKey) {
          if (event.shiftKey) {
            onReplaceAll?.();
          } else {
            onReplace?.();
          }
        }
      }
    },
    [onClose, onReplace, onReplaceAll],
  );
  const matchDisplay =
    matchCount > 0 ? `${currentMatchIndex + 1}/${matchCount}` : 'No matches';
  const hasMatches = matchCount > 0;

  return (
    <Box
      borderTopWidth="1px"
      borderColor="border.default"
      bg="bg.canvas"
      flexShrink={0}
      p="1"
    >
      {/* Search Row */}
      <HStack gap="1" alignItems="center" w="full">
        <Box flex="1" minW="0" position="relative">
          <Search01Icon
            size={12}
            style={{
              position: 'absolute',
              left: '6px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--colors-fg-muted)',
              pointerEvents: 'none',
            }}
          />
          <Input
            ref={searchInputRef}
            size="xs"
            h="7"
            fontSize="xs"
            placeholder="Search..."
            value={searchTerm}
            w="full"
            pl="6"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              onSearchTermChange(event.target.value);
            }}
            onKeyDown={handleSearchKeyDown}
          />
        </Box>

        {searchTerm && (
          <Text
            fontSize="xs"
            color="fg.muted"
            whiteSpace="nowrap"
            flexShrink={0}
            minW="50px"
            textAlign="center"
          >
            {matchDisplay}
          </Text>
        )}

        {/* Navigation buttons */}
        <Tooltip title="Previous match (Shift+Enter)">
          <IconButton
            size="xs"
            variant="ghost"
            h="7"
            w="7"
            minW="7"
            flexShrink={0}
            onClick={onPrevMatch}
            disabled={!hasMatches}
            aria-label="Previous match"
          >
            <ArrowLeft01Icon size={14} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Next match (Enter)">
          <IconButton
            size="xs"
            variant="ghost"
            h="7"
            w="7"
            minW="7"
            flexShrink={0}
            onClick={onNextMatch}
            disabled={!hasMatches}
            aria-label="Next match"
          >
            <ArrowRight01Icon size={14} />
          </IconButton>
        </Tooltip>

        {/* Regex toggle */}
        <Tooltip title="Use regular expression">
          <IconButton
            size="xs"
            variant={isRegex ? 'solid' : 'ghost'}
            h="7"
            w="7"
            minW="7"
            flexShrink={0}
            onClick={onRegexToggle}
            aria-label="Toggle regex"
          >
            <CodeIcon size={14} />
          </IconButton>
        </Tooltip>

        {/* Case sensitivity toggle */}
        <Tooltip title="Match case">
          <IconButton
            size="xs"
            variant={isCaseSensitive ? 'solid' : 'ghost'}
            h="7"
            w="7"
            minW="7"
            flexShrink={0}
            onClick={onCaseSensitiveToggle}
            aria-label="Toggle case sensitivity"
          >
            <LetterSpacingIcon size={14} />
          </IconButton>
        </Tooltip>

        {!hideReplace && onReplaceExpandedToggle && (
          <Tooltip title={isReplaceExpanded ? 'Hide replace' : 'Show replace'}>
            <IconButton
              size="xs"
              variant={isReplaceExpanded ? 'solid' : 'ghost'}
              h="7"
              w="7"
              minW="7"
              flexShrink={0}
              onClick={onReplaceExpandedToggle}
              aria-label="Toggle replace"
            >
              {isReplaceExpanded ? (
                <ArrowUp01Icon size={14} />
              ) : (
                <ArrowDown01Icon size={14} />
              )}
            </IconButton>
          </Tooltip>
        )}

        {/* Close button */}
        <Tooltip title="Close search (Escape)">
          <IconButton
            size="xs"
            variant="ghost"
            h="7"
            w="7"
            minW="7"
            flexShrink={0}
            onClick={onClose}
            aria-label="Close search"
          >
            <Cancel01Icon size={14} />
          </IconButton>
        </Tooltip>
      </HStack>

      {showReplaceRow && (
        <HStack gap="1" alignItems="center" w="full" mt="1">
          <Box flexShrink={0} color="fg.muted" pl="1">
            <ReplaceIcon size={14} />
          </Box>

          <Box flex="1" minW="0">
            <Input
              size="xs"
              h="7"
              fontSize="xs"
              placeholder="Replace with..."
              value={replaceTerm}
              w="full"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                onReplaceTermChange?.(event.target.value);
              }}
              onKeyDown={handleReplaceKeyDown}
            />
          </Box>

          <Tooltip title="Replace current (Cmd/Ctrl+Enter)">
            <Button
              size="xs"
              variant="outline"
              h="7"
              px="2"
              flexShrink={0}
              onClick={onReplace}
              disabled={!hasMatches}
            >
              <ReplaceIcon size={12} />
              <Text fontSize="xs" ml="1">
                {t('replace')}
              </Text>
            </Button>
          </Tooltip>

          <Tooltip title="Replace all (Cmd/Ctrl+Shift+Enter)">
            <Button
              size="xs"
              variant="outline"
              h="7"
              px="2"
              flexShrink={0}
              onClick={onReplaceAll}
              disabled={!hasMatches}
            >
              <ReplaceAllIcon size={12} />
              <Text fontSize="xs" ml="1">
                {t('replaceAll')}
              </Text>
            </Button>
          </Tooltip>
        </HStack>
      )}
    </Box>
  );
}
